import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSearchParams, useNavigate } from 'react-router-dom';
import GlobalSVG from "../../utils/GlobalSVG";
import deleteIcon from "../../assets/delete.png";
import API from "../../utils/API";
import Swal from "sweetalert2";

const POSITION_ORDER = ['Chão', '1/2 Elevação', 'Elevado', 'Chão / Elevado', 'Chão / 1/2 Elevação', 'Sem posição'];

const statusOptions = ["Bom", "Regular", "Ruim"];

const statusColors = {
    Bom: { active: 'bg-green-600 text-white', inactive: 'bg-white border border-gray-300 text-gray-700 hover:bg-green-50' },
    Regular: { active: 'bg-yellow-500 text-white', inactive: 'bg-white border border-gray-300 text-gray-700 hover:bg-yellow-50' },
    Ruim: { active: 'bg-red-600 text-white', inactive: 'bg-white border border-gray-300 text-gray-700 hover:bg-red-50' },
};

const cardBorderColor = {
    Bom: 'border-l-4 border-l-green-500',
    Regular: 'border-l-4 border-l-yellow-400',
    Ruim: 'border-l-4 border-l-red-500',
};

function groupItemsByPosition(itemsArray) {
    // itemsArray: [{checklist_item, position, answer_type}, ...]
    // Returns: { position: { itemName: {status, observation, answerType} } }
    //
    // YES_NO items are inserted FIRST in each position so they always render
    // before regular STATUS items (object key insertion order is preserved
    // when we later iterate with Object.entries).
    const groups = {};
    // Pass 1: YES_NO items first
    for (const item of itemsArray) {
        if ((item.answer_type || 'STATUS') !== 'YES_NO') continue;
        const pos = item.position || 'Sem posição';
        if (!groups[pos]) groups[pos] = {};
        groups[pos][item.checklist_item] = {
            status: '',
            observation: '',
            answerType: 'YES_NO',
        };
    }
    // Pass 2: STATUS (Bom/Regular/Ruim) items afterwards
    for (const item of itemsArray) {
        const answerType = item.answer_type || 'STATUS';
        if (answerType === 'YES_NO') continue;
        const pos = item.position || 'Sem posição';
        if (!groups[pos]) groups[pos] = {};
        groups[pos][item.checklist_item] = {
            status: 'Bom',
            observation: '',
            answerType,
        };
    }
    // Sort by POSITION_ORDER
    const sorted = {};
    for (const pos of POSITION_ORDER) {
        if (groups[pos]) sorted[pos] = groups[pos];
    }
    for (const pos of Object.keys(groups)) {
        if (!sorted[pos]) sorted[pos] = groups[pos];
    }
    return sorted;
}

function flattenChecklist(grouped) {
    // Flatten grouped state back to { itemName: {status, observation} }
    const flat = {};
    for (const pos of Object.keys(grouped)) {
        for (const [name, val] of Object.entries(grouped[pos])) {
            flat[name] = val;
        }
    }
    return flat;
}

export default function DetailedChecklist(props) {
    const [searchParams] = useSearchParams();
    const id = searchParams.get('id');
    const navigate = useNavigate();
    const [inputValue, setInputValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [mockServices, setMockServices] = useState([]);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [services, setServices] = useState({
        service_id: null,
        items: []
    });
    const [isLoading, setIsLoading] = useState(true);

    // Grouped state: { position: { itemName: {status, observation} } }
    const [checklistItems, setChecklistItems] = useState(null);
    const [rejectionReason, setRejectionReason] = useState(null);

    const autocompleteRef = useRef(null);

    useEffect(() => {
        setIsLoading(true);
        API.getServiceTypes(API.cancelToken()).then(res => {
            setMockServices(res.data.map(item => item.service_name));
        });

        API.getUserChecklistItems(API.cancelToken(), JSON.parse(localStorage.getItem("yc_usr_dt_ls")).company_id)
            .then(res => {
                if (res.data && res.data.length > 0) {
                    const grouped = groupItemsByPosition(res.data);
                    setChecklistItems(grouped);
                    setIsLoading(false);
                } else {
                    setChecklistItems({});
                    setIsLoading(false);
                }
            });
    }, []);

    useEffect(() => { console.log(checklistItems) }, [checklistItems]);

    useEffect(() => {
        if (id) {
            Swal.fire({
                title: 'Carregando dados',
                text: 'Aguarde um momento',
                showConfirmButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
                allowEnterKey: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            // Check if service was rejected by boss
            API.getOrder(API.cancelToken(), id).then(res => {
                if (res.data?.boss_approval_status === 'rejected' && res.data?.boss_rejection_reason) {
                    setRejectionReason(res.data.boss_rejection_reason);
                }
            }).catch(() => {});

            API.getChecklistDetail(API.cancelToken(), id)
                .then(res => {
                    setServices({
                        service_id: id,
                        items: res.data.items || []
                    });

                    if (res.data.checklist) {
                        // Merge existing checklist data (flat format) into grouped state
                        setChecklistItems(prev => {
                            if (!prev) return prev;
                            const updated = JSON.parse(JSON.stringify(prev));
                            for (const [itemName, itemVal] of Object.entries(res.data.checklist)) {
                                // Find which group contains this item and update it
                                let found = false;
                                for (const pos of Object.keys(updated)) {
                                    if (updated[pos][itemName] !== undefined) {
                                        updated[pos][itemName] = { ...updated[pos][itemName], ...itemVal };
                                        found = true;
                                        break;
                                    }
                                }
                                // If item not found in any group, add to 'Sem posição'
                                if (!found) {
                                    if (!updated['Sem posição']) updated['Sem posição'] = {};
                                    updated['Sem posição'][itemName] = itemVal;
                                }
                            }
                            return updated;
                        });
                    }

                    Swal.close();
                })
                .catch(err => {
                    console.error(err);
                    Swal.close();
                });
        }
    }, []);

    const filteredServices = mockServices.filter(service =>
        service.toLowerCase().includes(inputValue.toLowerCase())
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
        setIsOpen(true);
        setHighlightedIndex(-1);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex(prev =>
                prev < filteredServices.length - 1 ? prev + 1 : prev
            );
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        } else if (e.key === 'Enter' && highlightedIndex >= 0) {
            e.preventDefault();
            handleSelectService(filteredServices[highlightedIndex]);
        } else if (e.key === 'Escape') {
            setIsOpen(false);
        }
    };

    const handleSelectService = (service) => {
        setInputValue(service);
        setIsOpen(false);
        setHighlightedIndex(-1);
    };

    const hasExactMatch = inputValue.trim() && mockServices.some(
        s => s.toLowerCase() === inputValue.trim().toLowerCase()
    );

    const handleAddService = () => {
        if (inputValue.trim() && !services.items.some(service => service.service === inputValue)) {
            const newService = {
                service: inputValue,
                annotation: "",
                hht: ""
            };
            setServices(prevState => ({
                ...prevState,
                items: [newService, ...prevState.items]
            }));
            setInputValue('');
            setIsOpen(false);
        }
    };

    const handleCreateCustomServiceType = async () => {
        const name = inputValue.trim();
        if (!name) return;
        try {
            await API.addCustomServiceType(API.cancelToken(), { service_name: name });
            setMockServices(prev => [...prev, name].sort());
            handleAddService();
        } catch (error) {
            if (error.response?.status === 409) {
                handleAddService();
            } else {
                Swal.fire({
                    title: 'Erro',
                    text: 'Não foi possível criar o tipo de serviço',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
            }
        }
    };

    const handleServiceAnnotationChange = (index, event) => {
        const updatedItems = [...services.items];
        updatedItems[index].annotation = event.target.value;
        setServices(prevState => ({
            ...prevState,
            items: updatedItems
        }));
    };

    const handleServiceHhtChange = (index, event) => {
        const updatedItems = [...services.items];
        updatedItems[index].hht = event.target.value;
        setServices(prevState => ({
            ...prevState,
            items: updatedItems
        }));
    };

    const handleDeleteService = (index) => {
        setServices(prevState => ({
            ...prevState,
            items: prevState.items.filter((_, idx) => idx !== index)
        }));
    };

    const handleChecklistStatusChange = (position, item, status) => {
        setChecklistItems(prev => ({
            ...prev,
            [position]: {
                ...prev[position],
                [item]: { ...prev[position][item], status }
            }
        }));

        // ------------------------------------------------------------------
        // Um item avaliado como "Regular" ou "Ruim" precisa de reparo, então
        // já entra em Serviços Adicionais automaticamente — o mecânico só
        // completa HHT e observação, sem redigitar o nome do serviço.
        //
        // O vínculo é registrado em autoAddedFrom. Se o mecânico corrigir a
        // avaliação para "Bom", o serviço criado por essa avaliação sai da
        // lista; serviços digitados à mão nunca são removidos.
        // ------------------------------------------------------------------
        const answerType = checklistItems?.[position]?.[item]?.answerType || 'STATUS';
        if (answerType !== 'STATUS') return;

        const needsRepair = status === 'Regular' || status === 'Ruim';

        setServices(prevState => {
            const existingIndex = prevState.items.findIndex(
                (s) => s.autoAddedFrom === item
                    || String(s.service).toLowerCase() === String(item).toLowerCase()
            );

            if (needsRepair) {
                if (existingIndex !== -1) return prevState;   // já está na lista
                return {
                    ...prevState,
                    items: [
                        { service: item, annotation: '', hht: '', autoAddedFrom: item },
                        ...prevState.items,
                    ],
                };
            }

            // Voltou para "Bom": remove apenas se foi este item que o criou e
            // o mecânico ainda não escreveu nada nele.
            const existing = prevState.items[existingIndex];
            if (
                existingIndex === -1 ||
                existing.autoAddedFrom !== item ||
                existing.annotation?.trim() ||
                String(existing.hht ?? '').trim()
            ) {
                return prevState;
            }
            return {
                ...prevState,
                items: prevState.items.filter((_, idx) => idx !== existingIndex),
            };
        });
    };

    const handleChecklistObservationChange = (position, item, observation) => {
        setChecklistItems(prev => ({
            ...prev,
            [position]: {
                ...prev[position],
                [item]: { ...prev[position][item], observation }
            }
        }));
    };

    const validateChecklist = () => {
        if (!checklistItems) return false;
        for (const pos of Object.keys(checklistItems)) {
            const entries = Object.entries(checklistItems[pos]);
            const missing = entries.filter(([_, value]) => !value.status);
            if (missing.length > 0) {
                Swal.fire({
                    title: 'Checklist Incompleto',
                    text: 'Por favor, preencha o status de todos os itens do checklist',
                    icon: 'warning',
                    confirmButtonText: 'OK'
                });
                return false;
            }
            // YES_NO items answered 'Sim' must include an observation
            const missingObs = entries.filter(
                ([_, value]) => value.answerType === 'YES_NO' && value.status === 'Sim' && !value.observation?.trim()
            );
            if (missingObs.length > 0) {
                Swal.fire({
                    title: 'Descrição obrigatória',
                    text: `Descreva a observação para: ${missingObs.map(([k]) => k).join(', ')}`,
                    icon: 'warning',
                    confirmButtonText: 'OK'
                });
                return false;
            }
        }
        return true;
    };

    const moveOn = () => {
        if (!validateChecklist()) return;

        Swal.fire({
            title: 'Salvando serviços',
            text: 'Aguarde um momento',
            showConfirmButton: false,
            allowOutsideClick: false,
            allowEscapeKey: false,
            allowEnterKey: false,
            didOpen: () => {
                Swal.showLoading()
            }
        });

        // Flatten grouped checklist back to flat format for the API
        const flatChecklist = flattenChecklist(checklistItems);

        const dataToSend = {
            ...services,
            // autoAddedFrom é estado só de UI (rastreia qual item do checklist
            // gerou o serviço); não deve ir para a API.
            items: services.items.map(({ autoAddedFrom, ...item }) => item),
            service_id: id || services.service_id,
            checklist: flatChecklist,
            truncate: "NO"
        };

        API.postServiceItem(API.cancelToken(), dataToSend).then(res => {
            localStorage.setItem('service_items', JSON.stringify(dataToSend));
            Swal.close();
            // Concluída a análise, o mecânico volta para a home em vez de cair
            // na tela de orçamento, que é da equipe de compras.
            Swal.fire({
                title: 'Análise concluída!',
                text: 'A OS foi enviada para o chefe de oficina aprovar.',
                icon: 'success',
                confirmButtonText: 'Ok'
            }).then(() => navigate('/dashboard'));
        }).catch(err => {
            console.log(err);
            Swal.fire({
                title: 'Erro ao salvar serviços',
                text: 'Tente novamente mais tarde',
                icon: 'warning',
                confirmButtonText: 'OK'
            });
        });
    };

    return (
        <div className="flex flex-col px-11 py-4 items-center self-stretch bg-white rounded-sm shadow-sm max-md:px-5 max-md:max-w-full">
            <div className="w-full mt-8">
                {rejectionReason && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start gap-3">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <div>
                                <h3 className="text-sm font-semibold text-red-800">Checklist rejeitado pelo gestor</h3>
                                <p className="text-sm text-red-700 mt-1">{rejectionReason}</p>
                                <p className="text-xs text-red-500 mt-2">Corrija os itens indicados e envie novamente.</p>
                            </div>
                        </div>
                    </div>
                )}
                <h2 className="text-2xl font-medium mb-8">Checklist Obrigatório</h2>

                {isLoading ? (
                    <div>Carregando...</div>
                ) : (
                    checklistItems && Object.keys(checklistItems).length > 0 ? (
                        Object.entries(checklistItems).map(([position, items]) => (
                            <div key={position} className="mb-6">
                                <h3 className="text-lg font-semibold text-gray-600 mb-3 border-b border-gray-200 pb-2">
                                    {position}
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-2">
                                    {Object.keys(items).map((item) => {
                                        const entry = checklistItems[position][item];
                                        const currentStatus = entry.status;
                                        const isYesNo = entry.answerType === 'YES_NO';

                                        if (isYesNo) {
                                            const yesActive = currentStatus === 'Sim';
                                            const noActive = currentStatus === 'Não';
                                            return (
                                                <div key={item} className={`bg-gray-50 p-4 rounded-md ${yesActive ? 'border-l-4 border-l-red-500' : noActive ? 'border-l-4 border-l-green-500' : ''}`}>
                                                    <p className="font-medium mb-2">{item}</p>
                                                    <div className="flex gap-2 mb-2">
                                                        <button
                                                            onClick={() => handleChecklistStatusChange(position, item, 'Não')}
                                                            className={`px-3 py-1 rounded text-xs flex-1 transition-colors ${
                                                                noActive ? 'bg-green-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-green-50'
                                                            }`}
                                                        >
                                                            Não
                                                        </button>
                                                        <button
                                                            onClick={() => handleChecklistStatusChange(position, item, 'Sim')}
                                                            className={`px-3 py-1 rounded text-xs flex-1 transition-colors ${
                                                                yesActive ? 'bg-red-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-red-50'
                                                            }`}
                                                        >
                                                            Sim
                                                        </button>
                                                    </div>
                                                    {yesActive && (
                                                        <textarea
                                                            placeholder="Descreva qual luz / mensagem"
                                                            value={entry.observation}
                                                            onChange={(e) => handleChecklistObservationChange(position, item, e.target.value)}
                                                            className="w-full p-2 border border-gray-300 rounded-sm text-sm"
                                                            rows="2"
                                                        />
                                                    )}
                                                </div>
                                            );
                                        }

                                        return (
                                            <div key={item} className={`bg-gray-50 p-4 rounded-md ${cardBorderColor[currentStatus] || ''}`}>
                                                <p className="font-medium mb-2">{item}</p>
                                                <div className="flex gap-2 mb-2">
                                                    {statusOptions.map((status) => (
                                                        <button
                                                            key={status}
                                                            onClick={() => handleChecklistStatusChange(position, item, status)}
                                                            className={`px-3 py-1 rounded text-xs flex-1 transition-colors ${currentStatus === status
                                                                ? statusColors[status].active
                                                                : statusColors[status].inactive
                                                            }`}
                                                        >
                                                            {status}
                                                        </button>
                                                    ))}
                                                </div>
                                                <textarea
                                                    placeholder="Observações"
                                                    value={entry.observation}
                                                    onChange={(e) => handleChecklistObservationChange(position, item, e.target.value)}
                                                    className="w-full p-2 border border-gray-300 rounded-sm text-sm"
                                                    rows="2"
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-gray-500">Nenhum item de checklist configurado.</div>
                    )
                )}
            </div>

            <div className="w-full mt-8">
                <h2 className="text-xl font-medium mb-4">Serviços Adicionais</h2>
                <div className="flex flex-col items-center gap-6 self-stretch">
                    <div className="flex justify-start h-12 items-center gap-4 self-stretch">
                        <div className="w-full h-full flex" ref={autocompleteRef}>
                            <div className="relative w-full">
                                <input
                                    type="text"
                                    value={inputValue}
                                    onChange={handleInputChange}
                                    onKeyDown={handleKeyDown}
                                    onFocus={() => setIsOpen(true)}
                                    className="h-full border border-gray-300 rounded-sm pl-5 pr-4 py-1 focus:outline-none focus:ring-blue-500 focus:border-blue-500 w-full"
                                    placeholder="Buscar um item..."
                                    autoComplete="off"
                                />
                                <div className="absolute w-auto h-auto inset-y-0 right-0 p-2.5 flex items-center justify-center gap-2 self-stretch pointer-events-none">
                                    {GlobalSVG.search()}
                                </div>
                                {isOpen && (filteredServices.length > 0 || inputValue.trim()) && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-sm shadow-lg">
                                        {inputValue.trim() && !hasExactMatch && (
                                            <div
                                                className="px-4 py-2 cursor-pointer hover:bg-green-50 border-b border-gray-200 text-[#389E0D] font-medium flex items-center gap-2 sticky top-0 bg-white"
                                                onClick={handleCreateCustomServiceType}
                                            >
                                                <span className="text-lg">+</span>
                                                Criar novo serviço: "{inputValue.trim()}"
                                            </div>
                                        )}
                                        <div className="max-h-60 overflow-auto">
                                            {filteredServices.map((service, index) => (
                                                <div
                                                    key={service}
                                                    className={`px-4 py-2 cursor-pointer ${index === highlightedIndex ? 'bg-gray-100' : ''
                                                        } hover:bg-gray-100`}
                                                    onClick={() => handleSelectService(service)}
                                                    onMouseEnter={() => setHighlightedIndex(index)}
                                                >
                                                    {service}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div
                            className="flex w-auto px-4 py-1 justify-center items-center bg-[#389E0D] gap-2.5 border border-[#389E0D] rounded shadow-sm cursor-pointer h-full "
                            onClick={handleAddService}
                        >
                            <p className="text-[#FFF] whitespace-nowrap">Adicionar Serviço</p>
                        </div>
                    </div>

                    <div className="w-full p-4">
                        {services.items.map((service, index) => (
                            <div key={index} className="flex flex-col p-2 bg-gray-100 rounded-md mb-2">
                                <div className="flex mb-3 items-center">
                                    <label className="align-bottom w-36 font-semibold mr-2">Serviço a fazer:</label>
                                    <input
                                        className="p-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 w-full"
                                        value={service.service}
                                        readOnly
                                    />
                                    <img
                                        src={deleteIcon}
                                        alt="Remover serviço"
                                        className="w-6 h-6 ml-2 mr-2 cursor-pointer"
                                        onClick={() => handleDeleteService(index)}
                                    />
                                </div>
                                <div className="flex mb-3 items-center">
                                    <label className="align-bottom w-36 font-semibold mr-2">HHT (horas):</label>
                                    <select
                                        className="p-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 w-32 bg-white"
                                        value={service.hht ?? ""}
                                        onChange={(e) => handleServiceHhtChange(index, e)}
                                    >
                                        <option value="">Selecione...</option>
                                        {Array.from({ length: 40 }, (_, i) => (i + 1) * 0.5).map((v) => (
                                            <option key={v} value={v}>{String(v).replace('.', ',')}</option>
                                        ))}
                                    </select>
                                </div>
                                <textarea
                                    className="p-2 w-full border border-gray-300 rounded-sm"
                                    placeholder="Peças Relacionadas"
                                    onChange={(e) => handleServiceAnnotationChange(index, e)}
                                    value={service.annotation}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex px-3 py-6 h-16 w-full flex-row justify-end items-center gap-4 self-stretch">
                <div
                    className="flex h-10 w-36 px-2 py-4 justify-center items-center border border-[#389E0D] rounded shadow-sm cursor-pointer text-[#389E0D]"
                    onClick={() => navigate('/create/service?step=3&id=' + id)}
                >
                    {GlobalSVG.arrowLeft()}
                    <p className="ml-2">Voltar</p>
                </div>
                <div
                    className="flex h-10 px-4 py-4 justify-center items-center bg-[#389E0D] gap-2.5 border border-[#389E0D] rounded shadow-sm cursor-pointer"
                    onClick={moveOn}
                >
                    {GlobalSVG.arrowRight()}
                    <p className="text-[#FFF]">Enviar para Chefe da Oficina</p>
                </div>
            </div>
        </div>
    );
}
